import React, { useMemo } from 'react';
import { Spin } from 'antd';
import { keepOneNull, keepOneOrLine, keepOnePercentOrLine } from '@/utils/utils';
import { formatDuration, formatLoadRate } from './utils';
import styles from './index.module.scss';
import iconRealTimeSpeed from '../../../../../../imgs/icon-实时速度@2x.png';
import iconRatedFlowRate from '../../../../../../imgs/icon-额定流速@2x.png';
import iconRealtimeLoadRate from '../../../../../../imgs/icon-实时负载率@2x.png';
import iconMaxLoadRate from '../../../../../../imgs/icon-最大负载率@2x.png';
import iconMinLoadRate from '../../../../../../imgs/icon-最小负载率@2x.png';
import iconLoadLow from '../../../../../../imgs/icon-负载060@2x.png';
import iconLoadMid from '../../../../../../imgs/icon-负载6080@2x.png';
import iconLoadHigh from '../../../../../../imgs/icon-负载80100@2x.png';
import iconLoadOver from '../../../../../../imgs/icon-负载over100@2x.png';
import { Button, EchartsWithTheme, Themes } from '@maxtropy/components';

const rangeIconMap: Record<string, string> = {
  low: iconLoadLow,
  mid: iconLoadMid,
  high: iconLoadHigh,
  over: iconLoadOver,
};

const PlantOverview = ({ data, loading, onDetail }: any) => {
  const cards = useMemo(() => {
    return [
      { label: '实时流速(Nm³/h)', value: keepOneOrLine(data?.realTimeSpeed), icon: iconRealTimeSpeed, row: 'top' },
      { label: '额定流速(Nm³/h)', value: keepOneOrLine(data?.ratedFlowRate), icon: iconRatedFlowRate, row: 'top' },
      { label: '实时负载率', value: formatLoadRate(data?.realTimeLoadRate), icon: iconRealtimeLoadRate, row: 'top' },
      { label: '今日最大负载率', value: formatLoadRate(data?.maxLoadRate), icon: iconMaxLoadRate, row: 'top' },
      { label: '今日最小负载率', value: formatLoadRate(data?.minLoadRate), icon: iconMinLoadRate, row: 'top' },
      ...(data?.durationDistribution ?? []).map((item: any) => ({
        label: `负载率${item.rangeName}`,
        value: (
          <>
            <span style={{ fontSize: 12, color: 'var(--mx-text-secondary-color)', fontWeight: 400 }}>持续</span>
            <span>{formatDuration(item.duration, item.durationPercent).split('(')[0]}</span>
          </>
        ),
        sub: `占比: ${keepOnePercentOrLine(item.durationPercent)}%`,
        icon: rangeIconMap[item.range] ?? iconLoadLow,
        row: 'duration',
      })),
    ];
  }, [data]);

  const chartOption = useMemo(
    () => ({
      color: ['#16DD8E'],
      backgroundColor: 'transparent',
      grid: {
        left: 20,
        right: 10,
        top: 20,
        bottom: 45,
        containLabel: true,
      },
      tooltip: { trigger: 'axis', valueFormatter: (value: number) => `${value}%` },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: data?.trendData?.map((item: any) => item.time) ?? [],
      },
      yAxis: {
        type: 'value',
        nameGap: 20,
      },
      series: [
        {
          name: '负载率',
          type: 'line',
          smooth: true,
          symbol: 'none',
          data: data?.trendData?.map((item: any) => keepOneNull(item.loadRate)) ?? [],
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(22, 221, 142, 0.20)' },
                { offset: 1, color: 'rgba(22, 221, 142, 0)' },
              ],
            },
          },
          markLine: {
            symbol: 'none',
            label: {
              show: true,
              color: '#FFCB47',
              position: 'insideEndTop',
              formatter: `平均负载率: ${keepOneOrLine(data?.avgLoadRate)}%`,
            },
            lineStyle: { color: '#FFCB47', type: 'dashed' },
            data: [{ yAxis: data?.avgLoadRate ?? 0 }],
          },
        },
      ],
      dataZoom: [
        {
          type: 'inside',
          xAxisIndex: 0,
          filterMode: 'none',
        },
        {
          type: 'slider',
          xAxisIndex: 0,
          height: 18,
          bottom: 8,
          filterMode: 'none',
        },
      ],
    }),
    [data]
  );

  return (
    <Spin spinning={loading}>
      <div className={`${styles.cards} ${styles.plantCards}`}>
        {cards.map(item => (
          <div
            className={`${styles.plantCard} ${
              item.row === 'duration' ? styles.plantCardDuration : styles.plantCardTop
            }`}
            key={item.label}
          >
            <img className={styles.plantIcon} src={item.icon} alt="" />
            <div className={styles.plantCardContent}>
              <div className={styles.plantCardLabel}>{item.label}</div>
              <div className={styles.plantCardValue}>{item.value}</div>
              {item.sub && <div>{item.sub}</div>}
            </div>
          </div>
        ))}
      </div>
      <div className={styles.chartTitle}>
        <span>负载率(%)</span>
        <Button type="link" style={{ padding: 0 }} onClick={() => onDetail()}>
          查看详情 &gt;&gt;
        </Button>
      </div>
      <div className={styles.chartBox}>
        <EchartsWithTheme theme={Themes.DARK} option={chartOption} style={{ height: '100%' }} />
      </div>
    </Spin>
  );
};

export default PlantOverview;
